<template>
  <ServiceUnavailable
    v-if="serviceUnavailable"
    :message="serviceUnavailableMessage"
    :retrying="retrying"
    :next-retry-seconds="nextRetrySeconds"
    @retry="handleRetry"
    @back="handleBack"
  />
  <div v-else class="bg_color">
    <!-- <el-button @click="handleClickLogin">模拟登录</el-button> -->
    <div class="qr_code">
      <div class="box">
        <div>
          <div class="wa_title">{{ $t('whatsapp.login.title') }}</div>
          <ul style="margin-top: 22px;">
            <li>{{ $t('wa.login.l1') }}</li>
            <li style="display: inline-flex;align-items: center">
              {{ $t("wa.login.l2.1") }}
              <img src="./img/more.png" style="width: 16px;height: 16px;margin: 0px 4px;">
              {{ $t("wa.login.l2.2") }}
              <img src="./img/setting.png" style="width: 16px;height: 16px;margin: 0px 4px;">
              {{ $t("wa.login.l2.3") }}
            </li>
            <li>{{ $t('wa.login.l3') }}</li>
            <li>{{ $t('wa.login.l4') }}</li>
            <li>{{ $t('wa.login.l5') }}</li>
            <li>{{ $t('wa.login.l6') }}</li>
            <li>{{ $t('wa.login.l7') }}</li>
            <li>{{ $t('wa.login.l8') }}</li>
          </ul>
        </div>
        <div
            v-loading="qrLoading"
            element-loading-background="rgba(245, 245, 245, 0.7)"
            style="cursor: pointer;"
            @click="handleQrContainerClick"
        >
          <img
              v-if="qrCodeUrl"
              :src="qrCodeUrl"
              style="width: 100%;height: 100%;object-fit: contain;"
              @load="handleQrImgLoad"
              @error="handleQrImgError"
          />
          <div v-else-if="qrError"
               style="width: 100%;height: 100%;display: flex;flex-direction: column;align-items: center;justify-content: center;color: #909399;font-size: 14px;">
            <div>{{ qrError }}</div>
            <div style="margin-top: 8px;font-size: 12px;">点击刷新二维码</div>
          </div>
        </div>
      </div>
      <div class="wa_notice">
        <div class="wa_notice_title">{{ $t('whatsapp.login.notice.title') }}</div>
        <ol class="wa_notice_list">
          <li>
            <span>{{ $t('whatsapp.login.notice.item1') }}</span>
            <span class="wa_notice_link" @click="handleOpenBanGuide">{{ $t('whatsapp.login.notice.guideLink') }}</span>
          </li>
          <li>{{ $t('whatsapp.login.notice.item2') }}</li>
        </ol>
      </div>
    </div>
  </div>
</template>

<script setup>
import {onActivated, onBeforeUnmount, onDeactivated, onMounted, ref} from "vue";
import {onBeforeRouteLeave, useRoute, useRouter} from "vue-router";
import {createWhatsAppQr, getUserInfoBySession, updateUserContactInfo} from "@/api/whatsapp";
import {Storage} from "@/utils/storage";
import ServiceUnavailable from "@/views/WhatsApp/Compontents/ServiceUnavailable.vue";

const router = useRouter()
const route = useRoute()
const WA_LOGIN_STORAGE_KEY = 'wa_login_user'
const WA_MANUAL_LOGOUT_KEY = 'wa_manual_logout'
const qrCodeUrl = ref("")
const qrLoading = ref(false)
const qrError = ref("")
const qrLoaded = ref(false)
const userLoading = ref(false)
let lastManualQrRefreshAt = 0
const manualQrRefreshTs = ref([])
let refreshTimer = null
let loginPollTimer = null
let qrExpireTimer = null
const isPollingLogin = ref(false)
const hasTriggeredSyncAfterQrLogin = ref(false)
const isPageActive = ref(true)

let loginPollStartedAt = 0

const QR_TTL_MS = 20 * 1000
const QR_MIN_REFRESH_INTERVAL_MS = 20 * 1000
const QR_MAX_REFRESH_IN_WINDOW = 3
const QR_REFRESH_WINDOW_MS = 60 * 1000
const qrRefreshTs = ref([])

const serviceUnavailable = ref(false)
const serviceUnavailableMessage = ref('')
const retrying = ref(false)
const apiFailCount = ref(0)
const API_FAIL_THRESHOLD = 2

const nextRetrySeconds = ref(0)
let autoRetryIndex = 0
let autoRetryTimer = null
let autoRetryCountdownTimer = null

const isAuthIssue = (err) => {
  try {
    const status = err?.response?.status
    if (status === 401 || status === 403) return true
  } catch (e) {
  }

  const msg = String(
    err?.response?.data?.message ||
    err?.response?.data?.msg ||
    err?.message ||
    err ||
    ''
  )

  if (!msg) return false
  if (msg.includes('Token失效')) return true
  if (msg.includes('未登录')) return true
  if (msg.toLowerCase().includes('unauthorized')) return true
  if (msg.toLowerCase().includes('forbidden')) return true
  return false
}

const stopAutoRetry = () => {
  if (autoRetryTimer) {
    clearTimeout(autoRetryTimer)
    autoRetryTimer = null
  }
  if (autoRetryCountdownTimer) {
    clearInterval(autoRetryCountdownTimer)
    autoRetryCountdownTimer = null
  }
  nextRetrySeconds.value = 0
}

const scheduleAutoRetry = () => {
  if (!serviceUnavailable.value) return
  if (retrying.value) return
  if (autoRetryTimer || autoRetryCountdownTimer) return

  const steps = [2, 5, 10, 20, 30]
  const idx = Math.min(Number(autoRetryIndex || 0), steps.length - 1)
  const waitSeconds = steps[idx]
  nextRetrySeconds.value = waitSeconds

  autoRetryCountdownTimer = window.setInterval(() => {
    if (!serviceUnavailable.value || retrying.value) {
      stopAutoRetry()
      return
    }
    if (nextRetrySeconds.value > 0) {
      nextRetrySeconds.value -= 1
    }
  }, 1000)

  autoRetryTimer = window.setTimeout(() => {
    autoRetryTimer = null
    stopAutoRetry()
    autoRetryIndex = Math.min(Number(autoRetryIndex || 0) + 1, steps.length - 1)
    if (!serviceUnavailable.value || retrying.value) return
    handleRetry()
  }, waitSeconds * 1000)
}

const resetServiceUnavailable = () => {
  apiFailCount.value = 0
  serviceUnavailable.value = false
  serviceUnavailableMessage.value = ''
  autoRetryIndex = 0
  stopAutoRetry()
}

const markApiFail = (err, fallbackMessage) => {
  if (isAuthIssue(err)) {
    stopAutoRetry()
    serviceUnavailable.value = false
    serviceUnavailableMessage.value = ''
    return
  }
  apiFailCount.value += 1
  if (apiFailCount.value >= API_FAIL_THRESHOLD) {
    serviceUnavailable.value = true
    serviceUnavailableMessage.value = String(err?.message || fallbackMessage || '接口请求失败或服务维护中，请稍后再试。')
    retrying.value = false
    scheduleAutoRetry()
  }
}

const setQrCodeBlob = (blob) => {
  if (!blob) return

  qrLoaded.value = false

  if (qrCodeUrl.value) {
    URL.revokeObjectURL(qrCodeUrl.value)
  }

  const qrBlob = blob instanceof Blob ? blob : new Blob([blob], {type: 'image/png'})
  qrCodeUrl.value = URL.createObjectURL(qrBlob)
}


onMounted(() => {
  checkLoginStatus()

  window.addEventListener('visibilitychange', handleVisibilityChange)

  // refreshTimer = window.setInterval(() => {
  // getQrCode()
  // }, 60 * 1000)
})

onBeforeUnmount(() => {
  if (refreshTimer) {
    clearInterval(refreshTimer)
    refreshTimer = null
  }
  if (qrExpireTimer) {
    clearTimeout(qrExpireTimer)
    qrExpireTimer = null
  }
  stopLoginPolling()
  if (qrCodeUrl.value) {
    URL.revokeObjectURL(qrCodeUrl.value)
    qrCodeUrl.value = ""
  }

  stopAutoRetry()

  window.removeEventListener('visibilitychange', handleVisibilityChange)
})

onActivated(() => {
  isPageActive.value = true
})

onDeactivated(() => {
  isPageActive.value = false
  stopLoginPolling()
})

onBeforeRouteLeave(() => {
  isPageActive.value = false
  stopLoginPolling()
})

const saveWaLoginUser = (user) => {
  try {
    Storage.set(WA_LOGIN_STORAGE_KEY, user || {})
  } catch (e) {
  }
}

const clearWaLoginUser = () => {
  try {
    Storage.remove(WA_LOGIN_STORAGE_KEY)
  } catch (e) {
  }
}

const parseUserInfoResult = (res) => {
  const payload = res?.result || res?.data?.result || res?.data || res
  return {
    id: payload?.id,
    pushName: payload?.pushName,
    name: payload?.name || payload?.pushName,
    picture: payload?.picture
  }
}

const getSessionUserInfoNoCache = (config = {}) => {
  return getUserInfoBySession(
    { _t: Date.now() },
    {
      clearCache: true,
      headers: {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
        ...(config?.headers || {})
      },
      ...(config || {})
    }
  )
}

const stopLoginPolling = () => {
  if (loginPollTimer) {
    clearTimeout(loginPollTimer)
    loginPollTimer = null
  }
  isPollingLogin.value = false
}

const clearQrExpireTimer = () => {
  if (qrExpireTimer) {
    clearTimeout(qrExpireTimer)
    qrExpireTimer = null
  }
}

const scheduleQrExpire = () => {
  clearQrExpireTimer()
  if (!qrCodeUrl.value) return
  const ttlMs = QR_TTL_MS
  qrExpireTimer = window.setTimeout(() => {
    qrExpireTimer = null
    if (!isPageActive.value || document.hidden) {
      stopLoginPolling()
      return
    }
    if (serviceUnavailable.value || retrying.value) {
      stopLoginPolling()
      return
    }
    if (!hasTriggeredSyncAfterQrLogin.value) {
      stopLoginPolling()
      qrLoaded.value = false
      qrError.value = '二维码已失效，请点击刷新二维码'
      if (qrCodeUrl.value) {
        URL.revokeObjectURL(qrCodeUrl.value)
        qrCodeUrl.value = ""
      }
    }
  }, Math.max(0, ttlMs))
}

const handleVisibilityChange = () => {
  if (document.hidden) {
    stopLoginPolling()
    return
  }
  if (hasTriggeredSyncAfterQrLogin.value) {
    stopLoginPolling()
    return
  }
  if (isPageActive.value && qrCodeUrl.value && !serviceUnavailable.value) {
    startLoginPolling()
  }
}

const handleQrLoginSuccess = async (info) => {
  if (hasTriggeredSyncAfterQrLogin.value) return
  hasTriggeredSyncAfterQrLogin.value = true
  stopLoginPolling()
  clearQrExpireTimer()

  try {
    await updateUserContactInfo()
  } catch (e) {
    // 同步失败不阻断登录跳转
  }

  const userToSave = {
    id: info?.id,
    pushName: info?.pushName,
    name: info?.name || info?.pushName,
    picture: info?.picture
  }
  try {
    Storage.remove(WA_MANUAL_LOGOUT_KEY)
  } catch (e) {
  }
  saveWaLoginUser(userToSave)
  router.replace({
    path: `/whatsapp/wahome`,
    query: {
      ...(route?.query || {})
    }
  })
}

const startLoginPolling = () => {
  if (isPollingLogin.value) return
  if (!qrCodeUrl.value) return
  isPollingLogin.value = true
  hasTriggeredSyncAfterQrLogin.value = false
  loginPollStartedAt = Date.now()

  if (loginPollTimer) {
    clearTimeout(loginPollTimer)
    loginPollTimer = null
  }

  const computeNextPollDelayMs = () => {
    const elapsed = Math.max(0, Date.now() - Number(loginPollStartedAt || 0))
    // 减少接口请求频率：前期稍快，后期逐步降低
    if (elapsed < 10 * 1000) return 2500
    return 3500
  }

  const withJitter = (ms) => {
    const base = Math.max(0, Number(ms || 0))
    const jitter = Math.floor(Math.random() * 300)
    return base + jitter
  }

  const scheduleNext = (delayMs) => {
    if (!isPollingLogin.value) return
    if (!isPageActive.value) {
      stopLoginPolling()
      return
    }
    if (document.hidden) {
      stopLoginPolling()
      return
    }

    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      stopLoginPolling()
      return
    }

    const maxPollMs = 5 * 60 * 1000
    if (loginPollStartedAt && Date.now() - loginPollStartedAt > maxPollMs) {
      stopLoginPolling()
      qrError.value = '二维码已过期'
      return
    }

    const nextDelay = delayMs === undefined || delayMs === null
      ? computeNextPollDelayMs()
      : Number(delayMs)

    loginPollTimer = window.setTimeout(async () => {
      loginPollTimer = null
      if (!isPollingLogin.value) return
      if (!isPageActive.value || document.hidden) {
        stopLoginPolling()
        return
      }
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        stopLoginPolling()
        return
      }
      if (serviceUnavailable.value || retrying.value) {
        scheduleNext(withJitter(2000))
        return
      }
      if (userLoading.value) {
        scheduleNext(withJitter(800))
        return
      }

      userLoading.value = true
      try {
        const res = await getSessionUserInfoNoCache({ errorPop: false })
        if (!isPollingLogin.value) return
        const info = parseUserInfoResult(res)
        if (info?.id && (info?.name || info?.pushName)) {
          await handleQrLoginSuccess(info)
          return
        }
      } catch (e) {
      } finally {
        userLoading.value = false
      }

      if (!isPollingLogin.value) return

      scheduleNext()
    }, withJitter(nextDelay))
  }

  scheduleNext(0)
}

const checkLoginStatus = () => {
  userLoading.value = true
  console.log('[WhatsApp Login] Checking login status...')

  try {
    const manualLogoutAt = Storage.get(WA_MANUAL_LOGOUT_KEY)
    if (manualLogoutAt) {
      console.log('[WhatsApp Login] Manual logout marker found, skip auto login')
      clearWaLoginUser()
      getQrCode()
      userLoading.value = false
      return Promise.resolve()
    }
  } catch (e) {
  }

  return getSessionUserInfoNoCache()
      .then((res) => {
        resetServiceUnavailable()
        console.log('[WhatsApp Login] Got user info:', res)
        const info = parseUserInfoResult(res)
        console.log('[WhatsApp Login] Parsed info:', info)
        if (info?.id && (info?.name || info?.pushName)) {
          stopLoginPolling()
          clearQrExpireTimer()
          console.log('[WhatsApp Login] Valid user info, saving and redirecting...')
          const userToSave = {
            id: info?.id,
            pushName: info?.pushName,
            name: info?.name || info?.pushName,
            picture: info?.picture
          }
          saveWaLoginUser(userToSave)
          console.log('[WhatsApp Login] Saved user:', userToSave)
          console.log('[WhatsApp Login] Storage after save:', Storage.get(WA_LOGIN_STORAGE_KEY))
          router.replace(`/whatsapp/wahome`)
          console.log('[WhatsApp Login] Redirected to wahome')
          return
        }
        console.log('[WhatsApp Login] Invalid info, clearing and showing QR')
        clearWaLoginUser()
        getQrCode()
      })
      .catch((err) => {
        console.log('[WhatsApp Login] API error:', err)
        markApiFail(err, '服务暂不可用，请稍后重试')
        getQrCode()
      })
      .finally(() => {
        userLoading.value = false
      })
}

const handleClickLogin = () => {
  saveWaLoginUser({
    id: 'test_wa_user_001',
    pushName: 'Test User',
    isMock: true
  })
  router.replace(`/whatsapp/wahome`)
}

//获取二维码
const getQrCode = () => {
  if (qrLoading.value) return Promise.resolve()

  const now = Date.now()
  const ts = Array.isArray(qrRefreshTs.value) ? qrRefreshTs.value : []
  const nextTs = ts.filter(t => typeof t === 'number' && now - t < QR_REFRESH_WINDOW_MS)

  if (nextTs.length > 0) {
    const lastAt = nextTs[nextTs.length - 1]
    if (typeof lastAt === 'number' && now - lastAt < QR_MIN_REFRESH_INTERVAL_MS) {
      const remainSec = Math.ceil((QR_MIN_REFRESH_INTERVAL_MS - (now - lastAt)) / 1000)
      qrError.value = `请${remainSec}秒后再刷新二维码`
      qrLoaded.value = false
      qrLoading.value = false
      return Promise.resolve()
    }
  }

  if (nextTs.length >= QR_MAX_REFRESH_IN_WINDOW) {
    qrError.value = '二维码刷新次数过多，请稍后再试'
    qrLoaded.value = false
    qrLoading.value = false
    qrRefreshTs.value = nextTs
    return Promise.resolve()
  }

  nextTs.push(now)
  qrRefreshTs.value = nextTs

  qrLoading.value = true
  qrError.value = ""
  clearQrExpireTimer()
  stopLoginPolling()
  return createWhatsAppQr()
      .then(res => {
        resetServiceUnavailable()
        setQrCodeBlob(res)
        scheduleQrExpire()
        startLoginPolling()
      })
      .catch((err) => {
        qrError.value = "二维码获取失败"
        stopLoginPolling()
        markApiFail(err, '二维码获取失败，服务可能维护中')
      })
      .finally(() => {
        qrLoading.value = false
      })
}

const handleRetry = () => {
  if (retrying.value) return
  retrying.value = true
  apiFailCount.value = 0
  serviceUnavailable.value = true
  serviceUnavailableMessage.value = ''
  stopAutoRetry()
  qrError.value = ''
  Promise.resolve()
    .then(() => checkLoginStatus())
    .finally(() => {
      retrying.value = false
      if (serviceUnavailable.value) {
        scheduleAutoRetry()
      }
    })
}

const handleBack = () => {
  try {
    router.back()
  } catch (e) {
    router.replace('/')
  }
}

const handleQrImgLoad = () => {
  qrLoaded.value = true
}

const handleQrImgError = () => {
  qrLoaded.value = false
  qrError.value = "二维码加载失败"
  if (qrCodeUrl.value) {
    URL.revokeObjectURL(qrCodeUrl.value)
    qrCodeUrl.value = ""
  }
}

const handleQrContainerClick = () => {
  if (qrLoading.value) return
  if (serviceUnavailable.value || retrying.value) return

  const now = Date.now()
  if (lastManualQrRefreshAt && now - lastManualQrRefreshAt < 1500) return

  const windowMs = QR_REFRESH_WINDOW_MS
  const maxInWindow = QR_MAX_REFRESH_IN_WINDOW
  const ts = Array.isArray(manualQrRefreshTs.value) ? manualQrRefreshTs.value : []
  const nextTs = ts.filter(t => typeof t === 'number' && now - t < windowMs)
  if (nextTs.length >= maxInWindow) {
    qrError.value = '刷新过于频繁，请稍后再试'
    manualQrRefreshTs.value = nextTs
    return
  }
  if (nextTs.length > 0) {
    const lastAt = nextTs[nextTs.length - 1]
    if (typeof lastAt === 'number' && now - lastAt < QR_MIN_REFRESH_INTERVAL_MS) {
      const remainSec = Math.ceil((QR_MIN_REFRESH_INTERVAL_MS - (now - lastAt)) / 1000)
      qrError.value = `请${remainSec}秒后再刷新二维码`
      manualQrRefreshTs.value = nextTs
      return
    }
  }
  nextTs.push(now)
  manualQrRefreshTs.value = nextTs

  lastManualQrRefreshAt = now

  stopLoginPolling()
  userLoading.value = false
  qrError.value = ''
  qrLoaded.value = false
  getQrCode()
}

const handleOpenBanGuide = () => {
  try {
    const routeInfo = router.resolve({path: '/whatsappdoc'})
    window.open(routeInfo.href, '_blank', 'noopener')
  } catch (e) {
    console.error('打开文档失败:', e)
  }
}


</script>


<style lang="less" scoped>
.bg_color {
  height: 235px;
  width: 100%;
  background: #25D366;
  position: relative;

  .qr_code {
    position: absolute;
    height: 610px;
    width: 1000px;
    background: white;
    top: 96px;
    left: 50%;
    border-radius: 5px;
    transform: translateX(-50%);

    display: flex;
    flex-direction: column;

    .box {
      padding: 45px;
      display: flex;
      height: auto;
      width: 100%;
      justify-content: space-between;
      align-items: center;

      flex: 0 0 auto;

      & > div:nth-of-type(1) {
        height: 350px;
        //background: red;
      }

      & > div:nth-of-type(2) {
        width: 350px;
        height: 350px;
        background: #f5f5f5;
        border-radius: 4px;
      }

      .wa_title {
        font-family: PingFangSC, PingFang SC;
        font-weight: 500;
        font-size: 20px;
        color: #45525C;
        line-height: 33px;
      }

      ul {
        list-style-position: inside;
        list-style-type: decimal;

        li {
          font-family: PingFangSC, PingFang SC;
          font-weight: 400;
          font-size: 13px;
          color: #45525C;
          line-height: 36px;
        }
      }
    }

    .wa_notice {
      margin: 0 45px 28px 45px;
      padding: 18px 22px;
      background: #FFF7E6;
      border-radius: 4px;
      color: #45525C;

      .wa_notice_title {
        font-family: PingFangSC, PingFang SC;
        font-weight: 600;
        font-size: 14px;
        line-height: 20px;
        margin-bottom: 10px;
      }

      .wa_notice_list {
        margin: 0;
        padding-left: 18px;
        list-style-position: outside;

        li {
          font-family: PingFangSC, PingFang SC;
          font-weight: 400;
          font-size: 13px;
          line-height: 24px;
          margin: 6px 0;
        }
      }

      .wa_notice_link {
        color: #3B6CFB;
        cursor: pointer;
        margin-left: 6px;
      }
    }
  }

}
</style>